import { useState } from 'react'
import { useContactForm, Toast } from '@lukhas/ui'
import { Mail, MessageSquare, Clock, Send, Loader2 } from 'lucide-react'
import SiteHeader from '../components/SiteHeader'
import SiteFooter from '../components/SiteFooter'

const brandFont = {
  fontFamily: '"Helvetica Neue", "HelveticaNeue-UltraLight", -apple-system, sans-serif',
  fontWeight: 100,
} as const

const bodyFont = {
  fontFamily: '"Helvetica Neue", sans-serif',
  fontWeight: 300,
} as const

function GlassCard({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`rounded-xl border bg-white/[0.055] border-white/[0.09] backdrop-blur-sm hover:bg-white/[0.08] hover:border-white/[0.14] transition-all duration-300 ${className}`}>
      {children}
    </div>
  )
}

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    honeypot: ''
  })

  const [showToast, setShowToast] = useState(false)

  const { submitForm, isSubmitting, error } = useContactForm({
    endpoint: '/api/contact',
    onSuccess: () => {
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
        honeypot: ''
      })
    }
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await submitForm(formData)
    setShowToast(true)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))
  }

  return (
    <div className="min-h-screen bg-consciousness-deep">
      <SiteHeader />

      <section className="pt-32 pb-16 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <div className="mb-6 flex justify-center">
            <MessageSquare className="w-16 h-16 text-trust-blue" strokeWidth={1} />
          </div>
          <h1
            className="text-5xl md:text-7xl tracking-[0.15em] mb-6 text-white"
            style={brandFont}
          >
            Get In Touch
          </h1>
          <p className="text-xl text-white/70 max-w-3xl mx-auto" style={bodyFont}>
            Have questions about LUKHAS consciousness technology? We&apos;re here to help.
          </p>
        </div>
      </section>

      <section className="py-8 px-6 pb-24">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8">
          <div className="space-y-6">
            <GlassCard>
              <div className="p-8">
                <div className="flex items-center gap-4 mb-4">
                  <Mail className="w-8 h-8 text-trust-blue" />
                  <div>
                    <h3 className="text-xl text-white" style={{ ...bodyFont, fontWeight: 300 }}>General Inquiries</h3>
                    <a href="mailto:hello@lukhas.com" className="text-trust-blue hover:underline" style={bodyFont}>
                      hello@lukhas.com
                    </a>
                  </div>
                </div>
              </div>
            </GlassCard>

            <GlassCard>
              <div className="p-8">
                <div className="flex items-center gap-4 mb-4">
                  <Send className="w-8 h-8 text-trust-blue" />
                  <div>
                    <h3 className="text-xl text-white" style={{ ...bodyFont, fontWeight: 300 }}>Technical Support</h3>
                    <a href="mailto:support@lukhas.com" className="text-trust-blue hover:underline" style={bodyFont}>
                      support@lukhas.com
                    </a>
                  </div>
                </div>
              </div>
            </GlassCard>

            <GlassCard>
              <div className="p-8">
                <div className="flex items-center gap-4 mb-4">
                  <Clock className="w-8 h-8 text-verified-green" />
                  <div>
                    <h3 className="text-xl text-white" style={{ ...bodyFont, fontWeight: 300 }}>Response Time</h3>
                    <p className="text-white/50" style={bodyFont}>Usually within 24-48 hours</p>
                  </div>
                </div>
              </div>
            </GlassCard>
          </div>

          <GlassCard>
            <div className="p-8">
              <h2 className="text-2xl tracking-wide mb-6 text-white" style={{ ...bodyFont, fontWeight: 300 }}>
                Send Us A Message
              </h2>
              <form className="space-y-4" onSubmit={handleSubmit}>
                <input
                  type="text"
                  name="honeypot"
                  value={formData.honeypot}
                  onChange={handleChange}
                  style={{ display: 'none' }}
                  tabIndex={-1}
                  autoComplete="off"
                />
                <div>
                  <label className="block text-sm text-white/50 mb-2" style={bodyFont}>Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.09] rounded-lg text-white focus:outline-none focus:border-trust-blue/50 transition-colors"
                    placeholder="Your name"
                    style={bodyFont}
                  />
                </div>
                <div>
                  <label className="block text-sm text-white/50 mb-2" style={bodyFont}>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.09] rounded-lg text-white focus:outline-none focus:border-trust-blue/50 transition-colors"
                    placeholder="you@example.com"
                    style={bodyFont}
                  />
                </div>
                <div>
                  <label className="block text-sm text-white/50 mb-2" style={bodyFont}>Subject</label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.09] rounded-lg text-white focus:outline-none focus:border-trust-blue/50 transition-colors"
                    placeholder="What's this about?"
                    style={bodyFont}
                  />
                </div>
                <div>
                  <label className="block text-sm text-white/50 mb-2" style={bodyFont}>Message</label>
                  <textarea
                    rows={6}
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.09] rounded-lg text-white focus:outline-none focus:border-trust-blue/50 resize-none transition-colors"
                    placeholder="Tell us how we can help..."
                    style={bodyFont}
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 rounded-full bg-trust-blue text-white text-sm tracking-[0.06em] hover:bg-trust-blue/90 transition-all disabled:opacity-50"
                  style={bodyFont}
                >
                  {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Send Message'}
                </button>
              </form>
            </div>
          </GlassCard>
        </div>
      </section>

      <SiteFooter
        columns={[
          {
            title: 'Platform',
            links: [
              { label: 'Home', href: '/' },
              { label: 'Solutions', href: '/solutions' },
              { label: 'Enterprise', href: '/enterprise' },
            ],
          },
          {
            title: 'Ecosystem',
            links: [
              { label: 'Developers', href: 'https://lukhas.dev', external: true },
              { label: 'App Store', href: 'https://lukhas.store', external: true },
            ],
          },
          {
            title: 'Legal',
            links: [
              { label: 'Privacy Policy', href: '/privacy' },
              { label: 'Terms of Service', href: '/terms' },
              { label: 'Cookie Policy', href: '/cookies' },
            ],
          },
        ]}
      />
      {showToast && (
        <Toast
          message={error || 'Message sent successfully!'}
          type={error ? 'error' : 'success'}
          onClose={() => setShowToast(false)}
        />
      )}
    </div>
  )
}
